import { getToken, setToken, removeToken } from '@/utils/auth'
import {
  login as loginApi,
  registerStep1, registerStep2, registerStep3, registerStep4,
  sendEmailCode,
  getProfile
} from '@/api/auth'
import { getAssets } from '@/api/account'
import { getKycStatus, submitBasicKyc as submitBasicKycApi, submitAdvancedKyc as submitAdvancedKycApi, updateProfile as updateProfileApi } from '@/api/user'

const state = {
  token: getToken() || '',
  uid: '',
  email: '',
  nickname: '',
  realName: '',
  idNumber: '',
  basicKyc: '0',
  advancedKyc: '0',
  usdtBalance: 0,
  usdBalance: 0,
  equity: 0,
  floatingPnl: 0,
  showLoginModal: false,
  redirectPath: '',
  registerToken: '',
  tradeEnabled: true,
  maintenanceMode: false
}

const getters = {
  token: s => s.token,
  isLoggedIn: s => !!s.token,
  showLoginModal: s => s.showLoginModal,
  redirectPath: s => s.redirectPath,
  isTradeEnabled: s => s.tradeEnabled !== false,
  isMaintenanceMode: s => s.maintenanceMode === true,
  isBasicKycApproved: s => s.basicKyc === '2',
  isAdvancedKycApproved: s => s.advancedKyc === '2',
  user: s => ({
    uid: s.uid,
    email: s.email,
    nickname: s.nickname,
    realName: s.realName,
    idNumber: s.idNumber,
    basicKyc: s.basicKyc,
    advancedKyc: s.advancedKyc,
    equity: s.equity
  }),
  balances: s => ({
    usdt: s.usdtBalance,
    usd: s.usdBalance
  })
}

const mutations = {
  SET_TOKEN(state, token) {
    state.token = token
    setToken(token)
  },
  SET_USER(state, info) {
    state.uid = info.uid || ''
    state.email = info.email || ''
    state.nickname = info.nickname || info.remark || ''
    state.realName = info.realName || ''
    state.idNumber = info.idNumber || info.idCard || ''
    // profile 接口返回 kycStatus / advancedKycStatus（camelCase）
    state.basicKyc = String(info.kycStatus ?? info.kycBasicStatus ?? info.basicKyc ?? '0')
    state.advancedKyc = String(info.advancedKycStatus ?? info.kycAdvancedStatus ?? info.advancedKyc ?? '0')
  },
  SET_KYC_STATUS(state, { basic, advanced }) {
    if (basic) {
      state.basicKyc = String(basic.status ?? '0')
      state.realName = basic.realName || state.realName
      state.idNumber = basic.idCard || basic.idNumber || state.idNumber
    }
    if (advanced) {
      state.advancedKyc = String(advanced.status ?? '0')
    }
  },
  SET_BALANCES(state, { usdt, usd }) {
    state.usdtBalance = usdt
    state.usdBalance = usd
    // 同步更新净值（余额 + 浮动盈亏）
    state.equity = Math.round((usd + state.floatingPnl) * 100) / 100
  },
  SET_EQUITY(state, { equity, floatingPnl }) {
    state.floatingPnl = floatingPnl
    state.equity = equity
  },
  SET_REGISTER_TOKEN(state, token) {
    state.registerToken = token
  },
  CLEAR_USER(state) {
    state.token = ''
    state.uid = ''
    state.email = ''
    state.nickname = ''
    state.realName = ''
    state.idNumber = ''
    state.basicKyc = '0'
    state.advancedKyc = '0'
    state.usdtBalance = 0
    state.usdBalance = 0
    state.equity = 0
    state.floatingPnl = 0
    state.redirectPath = ''
    state.registerToken = ''
    removeToken()
  },
  SET_SHOW_LOGIN(state, val) {
    state.showLoginModal = val
  },
  SET_REDIRECT_PATH(state, path) {
    state.redirectPath = path || ''
  },
  SET_TRADE_ENABLED(state, enabled) {
    state.tradeEnabled = enabled
  },
  SET_MAINTENANCE_MODE(state, val) {
    state.maintenanceMode = val
  },
  SET_NICKNAME(state, nickname) {
    state.nickname = nickname || ''
  }
}

const actions = {
  // 发送邮箱验证码
  async sendCode(_, { email, scene }) {
    const res = await sendEmailCode({ email, scene: scene || 'register' })
    if (res.code === 200) {
      return res
    }
    throw new Error(res.msg || '发送验证码失败')
  },

  // 登录
  async login({ commit, dispatch }, loginForm) {
    const res = await loginApi(loginForm)
    if (res.code === 200) {
      const data = res.data || {}
      const token = data.accessToken || data.access_token || data.token || res.accessToken || res.access_token
      if (!token) throw new Error('登录失败：未返回令牌')
      commit('SET_TOKEN', token)
      // 登录后拉取用户信息
      await dispatch('getUserInfo')
    } else {
      throw new Error(res.msg || '登录失败')
    }
  },

  // 注册步骤1 — 邮箱验证
  async registerStep1Action({ commit }, { email, code }) {
    const res = await registerStep1({ email, code })
    if (res.code === 200) {
      const token = res.data?.registerToken || res.data?.register_token || ''
      if (token) commit('SET_REGISTER_TOKEN', token)
      return token
    }
    throw new Error(res.msg || '邮箱验证失败')
  },

  // 注册步骤2 — 设置登录密码
  async registerStep2Action({ state, commit }, { email, password }) {
    const res = await registerStep2({
      email,
      password,
      confirmPassword: password,
      registerToken: state.registerToken
    })
    if (res.code === 200) {
      const token = res.data?.registerToken || res.data?.register_token || ''
      if (token) { commit('SET_REGISTER_TOKEN', token) }
      return token
    }
    throw new Error(res.msg || '设置密码失败')
  },

  // 注册步骤3 — 设置交易密码
  async registerStep3Action({ state, commit }, { email, tradePwd }) {
    const res = await registerStep3({
      email,
      tradePassword: tradePwd,
      confirmTradePassword: tradePwd,
      registerToken: state.registerToken
    })
    if (res.code === 200) {
      const token = res.data?.registerToken || res.data?.register_token || ''
      if (token) { commit('SET_REGISTER_TOKEN', token) }
      return token
    }
    throw new Error(res.msg || '设置交易密码失败')
  },

  // 注册步骤4 — 邀请码 + 完成
  async registerStep4Action({ state }, { email, inviteCode }) {
    const data = { email, registerToken: state.registerToken }
    if (inviteCode) data.inviteUid = inviteCode
    const res = await registerStep4(data)
    if (res.code === 200) {
      return res
    }
    throw new Error(res.msg || '注册失败')
  },

  // 获取用户信息
  async getUserInfo({ commit, dispatch }) {
    // 并行获取用户资料和资产
    // skipLogout: true — 让本函数统一判断认证失败，避免拦截器抢先登出
    const [profileRes, assetsRes] = await Promise.allSettled([
      getProfile({ skipLogout: true }),
      getAssets({ skipLogout: true })
    ])
    if (profileRes.status === 'fulfilled') {
      const data = profileRes.value.data || profileRes.value || {}
      commit('SET_USER', data)
    } else {
      // profile 获取失败，检查是否是认证错误
      const errMsg = (profileRes.reason && profileRes.reason.message) || ''
      if (errMsg.includes('认证') || errMsg.includes('令牌') || errMsg.includes('过期') || errMsg.includes('401') || errMsg.includes('未登录')) {
        throw new Error('认证令牌已过期')
      }
    }
    if (assetsRes.status === 'fulfilled') {
      const assets = assetsRes.value.data || assetsRes.value || {}
      commit('SET_BALANCES', {
        usdt: assets.usdtBalance || 0,
        usd: assets.usdBalance || 0
      })
      commit('account/SET_BALANCES', {
        usdt: assets.usdtBalance || 0,
        usd: assets.usdBalance || 0
      }, { root: true })
    }
    // 获取KYC状态（含脱敏姓名和证件号）
    await dispatch('fetchKycStatus')
  },

  // 获取KYC认证状态
  async fetchKycStatus({ commit }) {
    try {
      // skipLogout: true — KYC 状态获取失败不应触发全局登出
      const res = await getKycStatus({ skipLogout: true })
      if (res.code === 200) {
        const data = res.data || {}
        commit('SET_KYC_STATUS', {
          basic: data.basic || null,
          advanced: data.advanced || null
        })
      }
    } catch (e) {
      // KYC 状态获取失败不影响主流程
      console.warn('获取KYC状态失败:', e)
    }
  },

  // 提交基础KYC认证
  async submitBasicKyc({ dispatch }, form) {
    const res = await submitBasicKycApi({
      realName: form.realName,
      idCard: form.idNumber || form.idCard
    })
    if (res.code === 200) {
      // 提交成功后刷新KYC状态
      await dispatch('fetchKycStatus')
      return res
    }
    throw new Error(res.msg || '基础认证提交失败')
  },

  // 提交高级KYC认证
  async submitAdvancedKyc({ dispatch }, form) {
    const res = await submitAdvancedKycApi({
      idCardFront: form.frontImg || form.idCardFront,
      idCardBack: form.backImg || form.idCardBack,
      idCardHold: form.holdImg || form.idCardHold || undefined
    })
    if (res.code === 200) {
      // 提交成功后刷新KYC状态
      await dispatch('fetchKycStatus')
      return res
    }
    throw new Error(res.msg || '高级认证提交失败')
  },

  logout({ commit }) {
    commit('CLEAR_USER')
    commit('account/SET_BALANCES', { usdt: 0, usd: 0 }, { root: true })
  },

  // 修改个人资料（昵称等）
  async updateProfile({ commit }, data) {
    const res = await updateProfileApi(data)
    if (res.code === 200) {
      // 更新本地 state 中的 nickname
      if (data.nickname !== undefined) {
        commit('SET_NICKNAME', data.nickname)
      }
      return res
    }
    throw new Error(res.msg || '修改资料失败')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
